/**
 * SEO and JSON-LD for help center article routes (`/helpcenter/$` wildcard — multi-segment paths).
 * Doc frontmatter supplies title, description, and optional JSON-LD; long copy is truncated for SERPs.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { SEO_META_DESCRIPTION_MAX_CHARS, truncateMetaDescription } from '@web/src/lib/seo/seo-common'
import { baseUrl } from '@web/src/lib/utilsClient'

export type HelpcenterDocForSeo = {
  title?: string
  description?: string
  json_ld?: Record<string, unknown> | unknown[]
  content: string
}

const HELP_CENTER_INDEX_DESCRIPTION =
  'Spicy Friends help center: accounts, privacy, billing, events, groups, venues, and safety. Browse the sidebar or contact support from the site footer.'

function truncatePlainFromMarkdown(text: string, maxLength: number): string {
  const plain = text
    .replace(/#{1,6}\s/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_`]/g, '')
  const trimmed = plain.trim()
  if (trimmed.length <= maxLength) return trimmed
  return `${trimmed.slice(0, maxLength - 3).trim()}...`
}

/** Ensures Article/WebPage JSON-LD uses the canonical URL for this deploy. */
function docJsonLdWithCanonicalPage(jsonLd: unknown, pageUrl: string): unknown {
  if (jsonLd === undefined || jsonLd === null) return jsonLd
  if (Array.isArray(jsonLd)) return jsonLd
  if (typeof jsonLd !== 'object') return jsonLd
  const o = { ...(jsonLd as Record<string, unknown>) }
  const existing = o.mainEntityOfPage
  if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
    o.mainEntityOfPage = { ...(existing as Record<string, unknown>), '@id': pageUrl }
  } else {
    o.mainEntityOfPage = { '@type': 'WebPage', '@id': pageUrl }
  }
  return o
}

function buildTitle(docTitle: string | undefined, slug: string, appName: string): string {
  const name = appName.trim() || 'Spicy Friends'
  const topic = (docTitle?.trim() || slug.replace(/\//g, ' · ')).slice(0, 32).trim() || 'Help'
  return `${topic} | ${name}`
}

function buildDescription(doc: HelpcenterDocForSeo, slug: string): string {
  const max = SEO_META_DESCRIPTION_MAX_CHARS
  const raw =
    doc.description?.trim() ||
    (doc.content ? truncatePlainFromMarkdown(doc.content, max) : '') ||
    `Spicy Friends help for ${doc.title?.trim() || slug}: accounts, privacy, events, groups, venues, and community safety.`

  return raw.length > max ? truncateMetaDescription(raw, max) : raw
}

export type HelpcenterSlugHeadInput = {
  doc?: HelpcenterDocForSeo
  slug: string
}

export function helpcenterSlugPageHeadFromLoaderData(input: HelpcenterSlugHeadInput | null | undefined) {
  const doc = input?.doc
  const slug = input?.slug ?? ''
  if (!doc) {
    return seo({
      title: `Help & documentation | ${env.VITE_APP_NAME}`,
      description: HELP_CENTER_INDEX_DESCRIPTION,
      url: `${baseUrl()}/helpcenter/${slug}`,
      robots: robotsMetaForEnvironment({
        production: 'index, follow',
        staging: 'noindex,nofollow',
      }),
    })
  }

  const pageUrl = `${baseUrl()}/helpcenter/${slug}`
  const title = buildTitle(doc.title, slug, env.VITE_APP_NAME)
  const description = buildDescription(doc, slug)

  return seo({
    title,
    description,
    url: pageUrl,
    ...(doc.json_ld !== undefined ? { jsonLd: docJsonLdWithCanonicalPage(doc.json_ld, pageUrl) } : {}),
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
  })
}
